import React from 'react';
import { Loader2 } from 'lucide-react';

export type ButtonVariant =
  | 'primary'     // Orange brand gradient / primary fill
  | 'secondary'   // Neutral surface secondary fill
  | 'outline'     // Border with subtle hover fill
  | 'ghost'       // Transparent background with hover effect
  | 'danger'      // Red warning / deletion state
  | 'success'     // Emerald positive / confirm state
  | 'sky';        // Sky blue secondary brand state

export type ButtonSize =
  | 'xs'  // Compact table rows / mobile card actions (40px mobile -> 28px desktop)
  | 'sm'  // Toolbars, drawer actions (44px mobile -> 34px desktop)
  | 'md'  // Standard forms, primary CTA (48px mobile -> 40px desktop)
  | 'lg'; // Hero / splash actions (54px mobile -> 48px desktop)

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: React.ComponentType<{ className?: string }>;
  iconPosition?: 'left' | 'right';
  isLoading?: boolean;
  loadingText?: string;
  fullWidth?: boolean;
  as?: 'button' | 'a';
  href?: string;
  target?: string;
  rel?: string;
}

export function Button({
  variant = 'primary',
  size = 'md',
  icon: Icon,
  iconPosition = 'left',
  isLoading = false,
  loadingText,
  fullWidth = false,
  disabled,
  children,
  className = '',
  type = 'button',
  as = 'button',
  href,
  target,
  rel,
  ...props
}: ButtonProps) {
  const baseClasses =
    'inline-flex items-center justify-center font-bold transition-all duration-200 active:scale-[0.98] cursor-pointer select-none disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100';

  const variantClasses: Record<ButtonVariant, string> = {
    primary:
      'bg-brand-orange text-white hover:bg-brand-orange-light shadow-md hover:shadow-lg active:shadow-xs',
    secondary:
      'bg-surface-secondary border border-border text-text-primary hover:bg-surface-tertiary shadow-xs',
    outline:
      'border border-border bg-surface text-text-primary hover:bg-surface-hover shadow-xs',
    ghost:
      'bg-transparent text-text-secondary hover:text-text-primary hover:bg-surface-hover',
    danger:
      'bg-error/10 border border-error/20 text-error hover:bg-error/20',
    success:
      'bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20',
    sky:
      'bg-brand-sky/10 border border-brand-sky/20 text-brand-sky hover:bg-brand-sky/20',
  };

  // Multi-Device Ergonomic Touch Scaling:
  // Mobile (<768px): Apple HIG (44px min) / Material 3 (48px min) touch targets for thumbs
  // Desktop (>=768px): Sleek compact height for mouse cursor precision
  const sizeClasses: Record<ButtonSize, string> = {
    xs: 'min-h-[40px] px-3 py-1.5 text-xs md:min-h-0 md:px-2.5 md:py-1 md:text-[11px] rounded-lg gap-1.5 md:gap-1',
    sm: 'min-h-[44px] px-4 py-2.5 text-xs md:min-h-0 md:px-3 md:py-1.5 md:text-xs rounded-xl gap-2 md:gap-1.5',
    md: 'min-h-[48px] px-5 py-3 text-sm md:min-h-0 md:px-4 md:py-2.5 md:text-sm rounded-xl gap-2.5 md:gap-2',
    lg: 'min-h-[54px] px-6 py-3.5 text-base md:min-h-0 md:px-5 md:py-3 md:text-base rounded-2xl gap-3 md:gap-2.5',
  };

  const iconSizes: Record<ButtonSize, string> = {
    xs: 'h-4 w-4 md:h-3 md:w-3',
    sm: 'h-4 w-4 md:h-3.5 md:w-3.5',
    md: 'h-5 w-5 md:h-4 md:w-4',
    lg: 'h-6 w-6 md:h-5 md:w-5',
  };

  const isBtnDisabled = disabled || isLoading;
  const combinedClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${
    fullWidth ? 'w-full' : ''
  } ${className}`;

  const content = (
    <>
      {isLoading ? (
        <>
          <Loader2 className={`${iconSizes[size]} animate-spin shrink-0`} />
          {loadingText ? <span>{loadingText}</span> : children ? <span>{children}</span> : null}
        </>
      ) : (
        <>
          {Icon && iconPosition === 'left' && <Icon className={`${iconSizes[size]} shrink-0`} />}
          {children && <span>{children}</span>}
          {Icon && iconPosition === 'right' && <Icon className={`${iconSizes[size]} shrink-0`} />}
        </>
      )}
    </>
  );

  if (as === 'a' && href) {
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        className={combinedClasses}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      disabled={isBtnDisabled}
      className={combinedClasses}
      {...props}
    >
      {content}
    </button>
  );
}
